import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { departmentService } from "../../../../services/api/departmentService";
import { employeeService } from "../../../../services/api/employeeService";
import { adminService } from "../../../../services/api/adminService";
import { normalizeEmployee } from "../../../../utils/employeeUtils";
import Loading from "../../components/Loading";
import SearchAndFilterBar from "../../components/SearchAndFilterBar";
import EmployeeDetail from "../../components/EmployeeDetail/EmployeeDetail";
import StaffTable from "../../quan-ly-tai-khoan/danh-sach-tai-khoan/components/StaffTable";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";

const DetailDepartment = () => {
  const { id } = useParams();
  const { token, user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [staffLoading, setStaffLoading] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [department, setDepartment] = useState(null);
  const [rows, setRows] = useState([]);

  const [searchInput, setSearchInput] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: "view", // view | edit | create
    employeeId: null,
  });

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchValue(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const fetchDepartment = async () => {
      try {
        setLoading(true);
        const res = await departmentService.get_detail_department(token, id);
        setDepartment(res.data);
      } catch {
        toast.error("Không thể tải chi tiết phòng ban");
      } finally {
        setLoading(false);
      }
    };

    if (id && token) fetchDepartment();
    if (user?.role === "SuperAdmin") setCanDelete(true);
  }, [id, token, user?.role]);

  const fetchEmployees = async () => {
    try {
      setStaffLoading(true);
      const res = await employeeService.get_list_employees_of_department(
        token,
        department._id,
        {
          page: currentPage,
          limit: itemsPerPage,
          ...(searchValue ? { search_value: searchValue } : {}),
        },
      );

      setTotalPages(res?.pagination?.totalPages || res?.totalPages || 1);
      setRows(
        (res.data || []).map((emp, i) =>
          normalizeEmployee(emp, i, currentPage, itemsPerPage),
        ),
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Không thể tải danh sách nhân viên",
      );
    } finally {
      setStaffLoading(false);
    }
  };

  useEffect(() => {
    if (!department) return;
    fetchEmployees();
    // eslint-disable-next-line
  }, [department, currentPage, itemsPerPage, searchValue]);

  const handleSubmitEmployee = async (payload, isAddExisting = false) => {
    try {
      if (isAddExisting) {
        await employeeService.update_detail_employee(
          token,
          payload.employeeId,
          {
            departmentId: payload.departmentId,
            ...(payload.responsibleDepartments !== undefined
              ? {
                  responsibleDepartments: payload.responsibleDepartments,
                }
              : {}),
          },
        );
        toast.success("Thêm nhân viên vào phòng ban thành công");
      } else if (modalState.mode === "create") {
        await employeeService.create_employee(token, payload);
        toast.success("Tạo nhân viên mới thành công");
        setCurrentPage(1);
      } else if (modalState.mode === "edit") {
        await employeeService.update_detail_employee(
          token,
          modalState.employeeId,
          payload,
        );
        toast.success("Cập nhật nhân viên thành công");
      }

      closeModal();
      fetchEmployees();
    } catch {
      toast.error("Thao tác thất bại");
      throw new Error();
    }
  };

  const closeModal = () => {
    setModalState({ isOpen: false, mode: "view", employeeId: null });
  };

  const handleDelete = (id) => {
    setDeleteTarget(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setStaffLoading(true);
      await adminService.delete_staff(deleteTarget, token);
      toast.success("Xóa tài khoản thành công!");
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.message || "Không thể xóa tài khoản.");
    } finally {
      setStaffLoading(false);
    }
  };

  const modalTitle = useMemo(() => {
    if (modalState.mode === "create") return "Thêm nhân viên";
    if (modalState.mode === "edit") return "Chỉnh sửa nhân viên";
    return "Chi tiết nhân viên";
  }, [modalState.mode]);


  if (loading || !department) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="bg-white rounded-lg p-6 flex flex-col h-full min-h-0 gap-6">
        <div className="shrink-0">
          <SearchAndFilterBar
            searchPlaceholder="Tìm theo mã / tên nhân viên"
            searchValue={searchInput}
            onSearchChange={(e) => setSearchInput(e.target.value)}
            onAddClick={() =>
              setModalState({ isOpen: true, mode: "create", employeeId: null })
            }
            addButtonLabel="Thêm nhân viên"
            addButtonIcon="/icons/fi-plus-circle.svg"
          />
        </div>

        <div className="flex-1 min-h-0">
          <div className="h-full min-h-0">
            <StaffTable
              loading={staffLoading}
              data={rows}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              setView={(row) =>
                setModalState({
                  isOpen: true,
                  mode: "view",
                  employeeId: row._id || row.id,
                })
              }
              setEdit
              handleEdit={(id) =>
                setModalState({
                  isOpen: true,
                  mode: "edit",
                  employeeId: id,
                })
              }
              setDelete={canDelete}
              handleDelete={(id, item) => {
                handleDelete(item.staffId || item._id || id);
              }}
            />
          </div>
        </div>

        {modalState.isOpen && (
          <EmployeeDetail
            employeeId={modalState.employeeId}
            mode={modalState.mode}
            layout="popup"
            isOpen={modalState.isOpen}
            departmentId={department._id}
            onCancel={closeModal}
            onSuccess={(data, isExisting) => {
              handleSubmitEmployee(data, isExisting);
            }}
            title={modalTitle}
          />
        )}

        <ConfirmDeleteModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteTarget(null);
          }}
          onConfirm={confirmDelete}
          title="Xác nhận xóa tài khoản"
          message="Bạn có chắc chắn muốn xóa tài khoản này không?"
          loading={staffLoading}
        />
      </div>
    </div>
  );
};

export default DetailDepartment;
